import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useTranslation } from '../i18n/LanguageContext';
import {
  Crown,
  Clock,
  Check,
  X,
  AlertTriangle,
  Shield,
  Loader2,
  CheckCircle2,
  RefreshCcw,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { paymentService } from '../services/payment/paymentService';
import { premiumAccessService } from '../services/premiumAccessService';

function SubscriptionPage() {
  const { t } = useTranslation();
  const { logout, refreshUser } = useContext(AuthContext);

  const [subInfo, setSubInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadSubscription();
    checkTrialAccess();
  }, []);

  const checkTrialAccess = async () => {
    try {
      const res = await axios.get('/subscription/check-access');
      if (res.data.forced_logout) {
        toast.error(res.data.message || 'Session expiree');
        logout();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  const loadSubscription = async () => {
    try {
      const [subRes, payRes] = await Promise.all([
        premiumAccessService.getSubscriptionStatus(true),
        axios.get('/subscription/payments'),
      ]);
      setSubInfo(subRes);
      setPayments(payRes.data || []);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoPremium = async () => {
    setProcessing(true);
    try {
      const result = await paymentService.startPremiumCheckout();
      if (!result.success) {
        toast.error(result.message || 'Impossible de demarrer le paiement premium.');
        return;
      }
      toast.success(result.message || 'Abonnement premium active.');
      premiumAccessService.clearCache();
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      await loadSubscription();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Erreur lors de l\'activation premium.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRestorePurchases = async () => {
    setRestoring(true);
    try {
      const result = await paymentService.restorePurchases();
      if (!result.success) {
        toast.error(result.message || 'Restauration impossible.');
        return;
      }
      toast.success(result.message || 'Achats restaures.');
      premiumAccessService.clearCache();
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      await loadSubscription();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Erreur restauration.');
    } finally {
      setRestoring(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post('/subscription/cancel');
      toast.success(res.data.message);
      premiumAccessService.clearCache();
      await loadSubscription();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur lors de l\'annulation';
      toast.error(detail);
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    const map = {
      TRIAL: 'bg-blue-100 text-blue-800',
      ACTIVE: 'bg-emerald-100 text-emerald-800',
      EXPIRED: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-200 text-gray-700',
      PENDING: 'bg-amber-100 text-amber-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const map = {
      TRIAL: <Clock className="w-5 h-5" />,
      ACTIVE: <Check className="w-5 h-5" />,
      EXPIRED: <AlertTriangle className="w-5 h-5" />,
      CANCELLED: <X className="w-5 h-5" />,
      PENDING: <Clock className="w-5 h-5" />,
    };
    return map[status] || <AlertTriangle className="w-5 h-5" />;
  };

  const getStatusLabel = (status) => {
    const map = {
      TRIAL: t('subscription.trialActive'),
      ACTIVE: t('subscription.active'),
      EXPIRED: t('subscription.expired'),
      CANCELLED: t('subscription.cancelled'),
      PENDING: 'Paiement en attente',
    };
    return map[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const canUpgrade = ['TRIAL', 'EXPIRED', 'CANCELLED', 'PENDING'].includes(subInfo?.status);

  return (
    <div className="space-y-4" data-testid="subscription-page">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
        <Shield className="w-4 h-4 text-blue-600 shrink-0" />
        <p className="text-xs text-blue-700">
          Paiement premium gere de maniere native sur Android via Google Play Billing.
        </p>
      </div>

      <Card data-testid="subscription-status-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getStatusColor(subInfo?.status)}`}>
                {getStatusIcon(subInfo?.status)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{getStatusLabel(subInfo?.status)}</h3>
                <p className="text-sm text-gray-600">{subInfo?.message}</p>
              </div>
            </div>
            {subInfo?.days_left > 0 && (
              <Badge className={getStatusColor(subInfo?.status)} data-testid="days-left-badge">
                {subInfo.days_left} {t('subscription.daysLeft')}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {canUpgrade && (
        <Card data-testid="premium-upgrade-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Crown className="w-5 h-5 text-emerald-600" />
              Passer Premium
            </CardTitle>
            <CardDescription>
              Debloquez les fonctionnalites premium avec un abonnement mensuel.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-emerald-50 rounded-lg p-4 text-center">
              <span className="text-3xl font-bold text-emerald-700">2,99 €</span>
              <span className="text-gray-600 ml-1">{t('subscription.price')}</span>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" /> Avantages Premium
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>Scan ticket de caisse (dans les limites de quota mensuel)</li>
                <li>Acces complet aux fonctionnalites premium</li>
                <li>Experience sans parcours de paiement externe</li>
              </ul>
            </div>

            <Button
              onClick={handleGoPremium}
              disabled={processing}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="go-premium-button"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Passer Premium
                </>
              )}
            </Button>

            <Button
              onClick={handleRestorePurchases}
              disabled={restoring}
              variant="outline"
              className="w-full h-11"
              data-testid="restore-purchases-button"
            >
              {restoring ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Restaurer mes achats
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Shield size={12} />
              <span>Paiement et renouvellement geres par Google Play</span>
            </div>
          </CardContent>
        </Card>
      )}

      {subInfo?.status === 'ACTIVE' && (
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={cancelling}
          className="w-full text-red-600 border-red-200 hover:bg-red-50 h-11"
          data-testid="cancel-subscription-button"
        >
          {cancelling ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <XCircle size={16} className="mr-2" />
          )}
          {t('subscription.cancel')}
        </Button>
      )}

      {payments.length > 0 && (
        <Card data-testid="payment-history-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              {t('subscription.paymentHistory')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {p.payment_provider === 'GOOGLE_PLAY'
                        ? 'Google Play'
                        : p.payment_provider === 'PAYPAL'
                          ? 'PayPal'
                          : `Carte ***${p.card_last4 || '****'}`}
                    </Badge>
                    <span className="text-sm text-gray-600">
                      {new Date(p.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <span className="font-medium text-emerald-700">{p.amount} €</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {subInfo?.status === 'ACTIVE' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Votre acces premium est actif.
        </div>
      )}
    </div>
  );
}

export default SubscriptionPage;
